import { Box, ButtonBase } from "@mui/material";
import { zoullooColors } from "../../theme";

interface SegmentedOption<T extends string | number> {
	value: T;
	label: string;
}

interface SegmentedControlProps<T extends string | number> {
	options: SegmentedOption<T>[];
	value: T;
	onChange: (value: T) => void;
	ariaLabel?: string;
}

const SegmentedControl = <T extends string | number>({
	options,
	value,
	onChange,
	ariaLabel
}: SegmentedControlProps<T>) => (
	<Box
		role="group"
		aria-label={ariaLabel}
		sx={{
			display: "inline-flex",
			gap: "2px",
			p: "2px",
			border: `1px solid ${zoullooColors.border.light}`,
			borderRadius: "9px",
			backgroundColor: zoullooColors.background.paper
		}}
	>
		{options.map(option => {
			const selected = option.value === value;
			return (
				<ButtonBase
					key={String(option.value)}
					aria-pressed={selected}
					onClick={() => onChange(option.value)}
					sx={{
						px: 1.5,
						py: 0.5,
						borderRadius: "7px",
						fontSize: "0.8125rem",
						fontWeight: 700,
						fontFamily: "inherit",
						whiteSpace: "nowrap",
						color: selected ? zoullooColors.text.white : zoullooColors.text.secondary,
						backgroundColor: selected ? zoullooColors.secondary.dark : "transparent",
						"&:hover": {
							backgroundColor: selected
								? zoullooColors.secondary.dark
								: zoullooColors.background.light
						},
						"&.Mui-focusVisible": {
							outline: `2px solid ${zoullooColors.secondary.main}`,
							outlineOffset: "1px"
						}
					}}
				>
					{option.label}
				</ButtonBase>
			);
		})}
	</Box>
);

export default SegmentedControl;
